/* -------------------------- Design imports start -------------------------- */

import { FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material'
import i18next from 'i18next'
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import MultilevelDrawer from '../../components/layout/MultilevelDrawer'

/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import { useUpdateCurrentUserPreferences } from '../../features/employee/hooks/useEmployeeMutations'



/* ------------------------- Functional imports end ------------------------- */

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
  formState?: {
    state: any | undefined
    setState: React.Dispatch<React.SetStateAction<any | undefined>>
  }
  onClose?: () => void
  onConfirm?: (input: any) => Promise<'SUCCESS' | 'ERROR'>
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */

export default function LanguageSelectionDrawer(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const { open, setOpen, formState, onClose, onConfirm } = props
  const { t } = useTranslation()
  const updatePreferencesMutation = useUpdateCurrentUserPreferences()
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */

  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const [currentLanguage, setCurrentLanguage] = useState(i18next.language)
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language')
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage)
      void (async () => {
        await i18next.changeLanguage(currentLanguage)
      })()
    }
  }, [])

  useEffect(() => {
    // Überprüfe, ob die aktuelle Sprache 'en-US' ist, und ändere sie zu 'en'
    if (currentLanguage) {
      if (currentLanguage === 'en-US') {
        setCurrentLanguage('en')
      }
    }
  }, [currentLanguage])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  // some API functions like fetch, create, ...
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleClose = (): void => {
    setOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const handleConfirm = async (): Promise<void> => {
    // change language on the frontend
    await i18next.changeLanguage(currentLanguage)

    // update user preferences in the backend
    try {
      await updatePreferencesMutation.mutateAsync({ language: currentLanguage })
      toast.success(t('common:feedback.success.successfullyChangedLanguage'))
      
      if (onConfirm) {
        await onConfirm(formState?.state)
      }
    } catch (error) {
      toast.error(t('common:feedback.error.failedToChangeLanguage'))
    }
  }

  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */

  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  return (
    <>
      <MultilevelDrawer
        open={open}
        setOpen={setOpen}
        title={t('common:content.label.settings') }
        size="small"
        onClose={handleClose}
        confirmButtonProps={{
          text: t('common:interaction.button.save'),
          onConfirm: () => void (async () => await handleConfirm())(),
          disabled: false,
        }}
      >
        <Grid container>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{t('common:content.label.language')}</InputLabel>
              <Select
                id="languageSelect"
                value={currentLanguage}
                label="Language"
                onChange={langSelect => setCurrentLanguage(langSelect.target.value)}
              >
                <MenuItem id="english" value={'en'} onClick={() => setCurrentLanguage('en')}>
                  {t('common:content.label.english')}
                </MenuItem>
                <MenuItem id="german" value={'de'} onClick={() => setCurrentLanguage('de')}>
                  {t('common:content.label.german')}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </MultilevelDrawer>
    </>
  )
}
